import { Box, Button, IconButton, Stack, Typography } from '@montugroup/design-system';
import InfoIcon from '@mui/icons-material/Info';
import { Fade, Paper, Popper, useTheme } from '@mui/material';
import type { ReactNode } from 'react';
import { useRef, useState } from 'react';

export type Props = {
  title: string;
  body: ReactNode;
};

export default function TableInfoDialog({ title, body }: Props) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const theme = useTheme();

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen((prev) => !prev);
  };

  const handleClose = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setOpen(false);
  };

  return (
    <>
      <IconButton ref={anchorRef} size="small" onClick={handleClick} onFocus={(e) => e.stopPropagation()}>
        <InfoIcon sx={{ height: '1.75rem', width: '1.75rem', padding: 0, color: '#F2EFE4', opacity: '56%' }} />
      </IconButton>

      {open && (
        <Box
          data-testid="overlay"
          onClick={handleClose}
          sx={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.4)',
            zIndex: theme.zIndex.modal - 1,
          }}
        />
      )}

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        transition
        style={{ zIndex: theme.zIndex.modal }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps}>
            <Paper
              elevation={8}
              sx={{
                width: '22.5rem',
                padding: '0.5rem',
                borderRadius: '0.25rem',
              }}
            >
              <Stack direction="row" gap={2} paddingTop={2}>
                <InfoIcon sx={{ marginTop: '2px' }} />
                <Stack>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {title}
                  </Typography>
                  <Typography variant="body2">{body}</Typography>
                </Stack>
              </Stack>

              <Stack direction="row" justifyContent="flex-end">
                <Button
                  variant="text"
                  size="small"
                  onClick={handleClose}
                  sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                >
                  Okay, got it
                </Button>
              </Stack>
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  );
}
